import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import P2 from '@base/P2/P2';
import Button from '@base/Button/Button';
import Link from '@base/Link/Link';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { nFormatter } from '@utils/textHelper';
import { breakpoints, mediaQuery, mobile, Spacings } from '@styles/index';
import {
  educatorValuePropsTypes,
  evtNameCategoryMap,
  ExEducatorDescription
} from '@constants/index';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { segment } from '@actions/analytics';
import { SEARCH_RESULT_CLICKED, SEARCH_RESULT_PAGE } from '@constants/segment';
import EducatorAvatar from '@cont/EducatorProfile/EducatorAvatar';
import camelCase from 'camelcase-keys';
import H7 from '@base/H7/H7';
import H5 from '@base/H5/H5';
import H6 from '@base/H6/H6';
import useWindowSize from '@hooks/useWindowSize';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import { EDUCATOR_TYPES } from '@cont/Batch/utils';
import EducatorProperties from './EducatorProperties';
import EducatorPhoto from './EducatorPhoto';

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-left: ${Spacings.SPACING_4B};
  flex: 1;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    margin-left: ${({ $isNewEducatorCard }) =>
      $isNewEducatorCard
        ? `${Spacings.SPACING_4B}`
        : `${Spacings.SPACING_14B}`};
  }
`;

const AuthorWrapper = styled.div`
  flex-direction: column;
`;

const AuthorNameWrapper = styled.div`
  margin-bottom: ${({ $isExEducator }) =>
    $isExEducator ? Spacings.SPACING_4B : Spacings.SPACING_1B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-left: ${({ $marginLeft }) => $marginLeft};
  }
`;

const AuthorName = styled.div`
  display: flex;
  align-items: center;
`;

const FooterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const AuthorDetails = styled(P2)`
  margin-bottom: ${Spacings.SPACING_4B};
  color: var(--color-text-secondary);
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  ${mediaQuery.uptoMobile} {
    -webkit-line-clamp: 3;
  }
`;

const EducatorPropertiesWrapper = styled.div`
  display: flex;
`;

const AuthorInfoOld = styled.div`
  ${mediaQuery.uptoMobile} {
    display: flex;
    align-items: flex-start;
  }
`;

const AuthorInfoNew = styled(AuthorInfoOld)`
  position: relative;
  width: ${Spacings.SPACING_30B};
  height: ${Spacings.SPACING_40B};
  background: var(--color-i-gray-27);
  border-radius: ${Spacings.SPACING_2B};
  overflow: hidden;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: flex;
    align-items: flex-start;
  }
`;

const Name = styled(H6)`
  @media only screen and (max-width: ${breakpoints.mobile}) {
    font-size: 14px;
  }
`;

const StyH5 = styled(H5)`
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  max-width: 250px;
`;

const EducatorDetailedCard = ({
  gridColumn,
  linkStyle,
  educator,
  showFollow,
  toggleFollow,
  dataAnalytics,
  index,
  useLeaderboardMetaInfo,
  className,
  type,
  updateRecentSearchLog,
  searchData,
  showBadge,
  showNewEducatorCard
}) => {
  const { isMobile } = useWindowSize();
  const { selectedGoal: goalData } = useSelectedGoal();
  const educatorLevelInfo = camelCase(educator?.levelInfo, { deep: true });

  const getLevelUid = () => {
    if (type === 'search') {
      const currentGoal = educatorLevelInfo?.goalWise?.find(
        (goalObject) => goalObject.goalId === goalData.uid
      );
      return currentGoal?.levelId || educatorLevelInfo?.maxLevelId;
    }
    return educatorLevelInfo?.levelId || educator?.levelId;
  };

  const goalSegmentData = useGoalEventProps({ goalUID: goalData.uid });
  const levelInfoData = {
    current_level_id: showBadge ? getLevelUid() : -1
  };

  const {
    thumbnail,
    avatar,
    firstName,
    lastName,
    name,
    isExEducator,
    liveMinutes,
    followersCount,
    description,
    stats,
    totalWatchMins,
    totalWatchTime = 0,
    username,
    leaderboardMetaInfo = {},
    uid,
    avatarV2,
    oldAvatar,
    fallbackAvatar,
    bio,
    educatorTypeV2
  } = educator;
  const dispatch = useDispatch();
  const isFollowing = useSelector(
    ({ users }) => users.data[username]?.isFollowing || false
  );

  const isPartnerEducator = educatorTypeV2 === EDUCATOR_TYPES.PARTNER;

  const watchMins =
    Math.round(stats?.statistics?.TOTAL_WATCH_TIME_MINUTES / 60) ||
    Math.round(totalWatchTime / 60) ||
    totalWatchMins ||
    liveMinutes;

  const getProps = () => {
    const valueProps = [];
    if (watchMins > 0)
      valueProps.push({
        display_string: 'Watch mins',
        value: watchMins,
        type: 1
      });
    if (!isExEducator)
      valueProps.push({
        display_string: 'Followers',
        value: followersCount,
        type: 1
      });
    return valueProps;
  };

  let valueProps = [];
  if (useLeaderboardMetaInfo) {
    valueProps = leaderboardMetaInfo?.value_props?.length
      ? leaderboardMetaInfo.value_props
      : getProps();
  } else {
    valueProps = getProps();
  }

  const anchorStyle = `
    height: 196px;
    background-color: var(--color-base-1);
    grid-column: ${gridColumn};
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_4B};
    display: flex;
    box-shadow: ${boxShadowGenerator()};
    border-radius: ${Spacings.SPACING_2B};
    cursor: pointer;
    flex: 1;
    :hover {
      box-shadow: ${boxShadowGenerator({
        yOffset: Spacings.SPACING_1B,
        blur: Spacings.SPACING_2B
      })};
    }
    transition: box-shadow 0.2s ease-in-out;
    @media only screen and (max-width: ${mobile.maxWidth}) {
      flex-direction: ${showNewEducatorCard ? 'row' : 'column'};
      height: auto;
      &.last{
        margin-bottom: 56px;
      }
    }
  `;

  const handleAnalytics = () => {
    if (type === 'search') {
      const nSearchData = { ...searchData };
      delete nSearchData.query;
      const position = nSearchData.position ? nSearchData.position : 'See all';
      segment.track(
        SEARCH_RESULT_CLICKED,
        {
          ...goalSegmentData,
          sub_position: index + 1,
          educator_uid: uid,
          educator_username: username,
          educator_name: name,
          last_primary_source: SEARCH_RESULT_PAGE,
          last_primary_source_section: 'Educator',
          search_section: 'Global',
          current_screen: 'Educator',
          eventName: SEARCH_RESULT_CLICKED,
          eventType: 'click',
          version: 2,
          objectID: `${searchData.object_type}:${uid}`,
          userToken: uid,
          ...nSearchData,
          position
        },
        evtNameCategoryMap.WITHOUT_CATEGORY
      );
      dispatch(
        updateRecentSearchLog({
          ...searchData,
          entity_id: `${searchData.object_type}:${uid}`,
          goal_uid: goalData.uid
        })
      );
    }
  };

  const getName = () => {
    const fullName = `${firstName} ${lastName}`;
    return name || fullName;
  };

  const renderAuthorInfo = () => {
    if (showNewEducatorCard) {
      return (
        <AuthorInfoNew>
          <EducatorPhoto
            levelInfo={levelInfoData}
            newThumbnail={avatarV2}
            oldThumbnail={oldAvatar}
            fallback={fallbackAvatar || avatar}
            isPartnerEducator={isPartnerEducator}
          />
        </AuthorInfoNew>
      );
    }
    return (
      <AuthorInfoOld>
        <EducatorAvatar
          src={thumbnail}
          levelInfo={levelInfoData}
          size="S"
          isPartnerEducator={isPartnerEducator}
        />
        {isMobile ? (
          <AuthorNameWrapper>
            <Name color="var(--color-text-primary)">{name}</Name>
            {/* isVerifiedEducator && <VerifiedIcon color="var(--color-i-blue)" /> */}
          </AuthorNameWrapper>
        ) : null}
      </AuthorInfoOld>
    );
  };

  return (
    <Link
      href="/[authorID]"
      as={`/@${username}`}
      anchorStyle={anchorStyle + linkStyle}
      onClick={handleAnalytics}
      className={className}
    >
      {renderAuthorInfo()}
      <InfoWrapper
        data-analytics={`${dataAnalytics}-educatorsCard`}
        $isNewEducatorCard={showNewEducatorCard}
      >
        <AuthorWrapper>
          {isMobile && !showNewEducatorCard ? null : (
            <AuthorNameWrapper
              $isExEducator={isExEducator}
              $marginLeft={showNewEducatorCard ? 0 : '12px'}
            >
              <AuthorName>
                <StyH5 color="var(--color-text-primary)" as="h4">
                  {getName()}
                </StyH5>
                {/* isVerifiedEducator && (
                  <VerifiedIcon color="var(--color-i-blue)" />
                ) */}
              </AuthorName>
              {isExEducator && (
                <H7 color="var(--color-text-secondary)">Ex-educator</H7>
              )}
            </AuthorNameWrapper>
          )}

          <AuthorDetails title={description}>
            {isExEducator ? ExEducatorDescription : description || bio}
          </AuthorDetails>
        </AuthorWrapper>
        <FooterWrapper>
          <EducatorPropertiesWrapper>
            {valueProps.slice(0, 3).map((valueProp) => {
              const {
                display_string: propLabel,
                value,
                type: nType
              } = valueProp;
              return (
                <EducatorProperties
                  key={propLabel}
                  label={propLabel}
                  stats={
                    nType === educatorValuePropsTypes.INTEGER
                      ? nFormatter(value)
                      : value
                  }
                />
              );
            })}
            {/* {watchMins > 0 && (
              <EducatorProperties
                label="Watch mins"
                stats={textHelper.nFormatter(watchMins)}
              />
            )}
            <EducatorProperties
              label="Followers"
              stats={textHelper.nFormatter(followersCount)}
            /> */}
          </EducatorPropertiesWrapper>
          {showFollow && (
            <Button
              label={isFollowing ? 'Following' : 'Follow'}
              variant={isFollowing ? 'hollow' : 'filled'}
              color={isFollowing ? 'black' : 'green'}
              grid={2}
              onClick={toggleFollow(username, !isFollowing)}
            />
          )}
        </FooterWrapper>
      </InfoWrapper>
    </Link>
  );
};

EducatorDetailedCard.propTypes = {
  gridColumn: PropTypes.string,
  linkStyle: PropTypes.string,
  educator: PropTypes.objectOf(PropTypes.any),
  showFollow: PropTypes.bool,
  toggleFollow: PropTypes.func,
  dataAnalytics: PropTypes.string,
  index: PropTypes.number.isRequired,
  useLeaderboardMetaInfo: PropTypes.bool,
  showBadge: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.string,
  updateRecentSearchLog: PropTypes.func,
  showNewEducatorCard: PropTypes.bool,
  searchData: PropTypes.objectOf(PropTypes.any)
};

EducatorDetailedCard.defaultProps = {
  gridColumn: 'span 6',
  educator: {},
  linkStyle: '',
  showFollow: false,
  toggleFollow: () => {},
  dataAnalytics: '',
  useLeaderboardMetaInfo: false,
  showBadge: false,
  className: '',
  type: '',
  updateRecentSearchLog: () => {},
  searchData: {},
  showNewEducatorCard: true
};

export default EducatorDetailedCard;
